import { cloneDeep, isFinite } from 'lodash-es';
import { MANUSCRIPT_V1_API_URL_PREFIX as URL_PREFIX } from '@/lib/constants/manuscripts/api';
import * as store from '@/lib/constants/manuscripts/store';

const useQueueStore = ({
  queueItemsPath = '/manuscripts/',
  guidSearchParam = 'studio_id',
  queueItemIdField = 'manuscript_id',
  queueItemIdFilterField = 'manuscript_id',
  queueItemSearchParams = {},
  queueItemUpdateFields = [
    'import_meta',
    'title',
    'status',
    'has_data',
  ],
} = {}) => {
  const queueStore = {
    state: () => ({
      isFetchingQueueItems: false,
      fetchQueueItemsError: null,
      queueItems: null,
      searchText: null,
      searchParams: null,
      sortField: 'created_on',
      sortDirection: 'desc',
      pagination: null,
      queueItemUpdatesTimeout: null,
      queueItemUpdatesEnabled: false,
    }),
    actions: {
      [store.SET_QUEUE_SEARCH_TEXT](value) {
        this.searchText = value;
      },
      [store.SET_QUEUE_SORT]({ direction, field }) {
        this.sortField = field;
        this.sortDirection = direction;
      },
      async [store.GET_QUEUE_ITEMS](query = {}) {
        try {
          this.isFetchingQueueItems = true;
          this.fetchQueueItemsError = null;

          const page = parseInt(query?.page, 10);

          if ('searchText' in query) {
            this.searchText = query.searchText || '';
          }

          if (query.sortField) {
            this.sortField = query.sortField;
          }

          if (['asc', 'desc'].includes(query.sortDirection)) {
            this.sortDirection = query.sortDirection;
          }

          const searchParams = {
            search_text: this.searchText || '',
            sort_direction: this.sortDirection,
            sort_field: this.sortField,
            page: isFinite(page) ? page : 1,
            per_page: 12,
            ...queueItemSearchParams,
          };

          const serverSearchParams = new URLSearchParams(cloneDeep(searchParams));

          const guidExp = /(([0-9a-fA-F]{8})-([0-9a-fA-F]{4}-){3}([0-9a-fA-F]{12}))/g;

          if (searchParams.search_text) {
            const guids = [...searchParams.search_text.matchAll(guidExp)]
              .map((match) => match[1]);

            if (guids.length) {
              serverSearchParams.set('search_text', '');

              guids.forEach((guid) => {
                serverSearchParams.append(guidSearchParam, guid);
              });
            }
          }

          const queryString = serverSearchParams.toString();

          const path = `${URL_PREFIX}${queueItemsPath}?${queryString}`;

          const { data: { results, pagination } } = await this.appApi(path);

          this.searchParams = searchParams;
          this.queueItems = results;
          this.pagination = pagination;

          this[store.START_QUEUE_ITEM_UPDATES]();
        } catch (error) {
          const serverError = error?.response?.data?.meta;
          this.fetchQueueItemsError = {
            ...serverError,
          };
        } finally {
          this.isFetchingQueueItems = false;
        }
      },
      [store.STOP_QUEUE_ITEM_UPDATES]() {
        if (this.queueItemUpdatesTimeout) {
          clearTimeout(this.queueItemUpdatesTimeout);
          this.queueItemUpdatesTimeout = null;
        }
      },
      [store.SET_QUEUE_ITEM_UPDATES_ENABLED](isEnabled) {
        if (!isEnabled) {
          this[store.STOP_QUEUE_ITEM_UPDATES]();
        }

        this.queueItemUpdatesEnabled = isEnabled;
      },
      [store.START_QUEUE_ITEM_UPDATES]() {
        this[store.STOP_QUEUE_ITEM_UPDATES]();

        const shouldStartUpdates = () => !!this.queueItems?.length && this.queueItemUpdatesEnabled;

        if (!shouldStartUpdates()) {
          return;
        }

        this.queueItemUpdatesTimeout = setTimeout(async () => {
          try {
            const queueItemIds = (this.queueItems || []).map((m) => m[queueItemIdField]);

            if (queueItemIds.length) {
              const queueItemIdPairs = queueItemIds.map((id) => [queueItemIdFilterField, id]);
              const searchParams = new URLSearchParams(queueItemIdPairs);

              Object.keys(queueItemSearchParams).forEach((paramKey) => {
                searchParams.append(paramKey, queueItemSearchParams[paramKey]);
              });

              const path = `${URL_PREFIX}${queueItemsPath}?${searchParams.toString()}`;

              const { data: { results } } = await this.appApi(path);

              results.forEach((result) => {
                const queueItemToUpdate = this.queueItems.find((m) => m.id === result.id);

                if (queueItemToUpdate) {
                  queueItemUpdateFields.forEach((field) => {
                    if (field in result) {
                      const serverValue = result[field];

                      if (queueItemToUpdate[field] !== serverValue) {
                        queueItemToUpdate[field] = serverValue;
                      }
                    }
                  });
                }
              });
            }
          } finally {
            if (shouldStartUpdates()) {
              this[store.START_QUEUE_ITEM_UPDATES]();
            }
          }
        }, store.QUEUE_ITEM_UPDATE_INTERVAL);
      },
    },
  };

  return queueStore;
};

export default useQueueStore;
